import { ConnectionErrorState } from '@/components/connectionError';
import { CallLink } from '@/features/store/pos-unavailable/styles';
import { isTransportFailure } from '@/utils/networkErrors';
import { toCallablePhone } from '@/utils/phoneNumber';

/**
 * TEAM-15314: product-owned copy for a menu read that came back with a real response — a server
 * error after the retries are spent, a not-found, or a body that is not menu data. These are not
 * the diner's connection, so they must not get the "check your connection" wording.
 */
export const MENU_LOAD_ERROR_COPY = {
  heading: "We couldn't load the menu.",
  /** Followed by the restaurant's phone number as a `tel:` link. */
  messageWithPhone: 'Try again, or call the restaurant at',
  messageWithoutPhone: 'Try again in a moment.',
};

interface Props {
  /** The failure from the menu read (`useMenu().error`). */
  error: unknown;
  /** The location's stored phone number (`location.phone_number`), if any. */
  phoneNumber?: string | null;
  onRetry: () => void;
}

/**
 * The store page's menu error state.
 *
 * A transport failure (no readable response — offline, DNS/TLS, a dropped request) keeps the
 * shared connection copy unchanged. Every other failure says the menu could not be loaded and
 * offers the restaurant's phone number when one is on file. Both reuse `ConnectionErrorState`
 * so the panel, `role="alert"` and the "Try again" button (with its duplicate-press guard) stay
 * identical. `isTransportFailure`, not `isNetworkError`: since TEAM-15165 the menu read surfaces a
 * bare network error, but a network failure wrapped one level deep in an `UpstreamRequestError`
 * (e.g. from the SDK) must still read as a transport failure, which `isTransportFailure` unwraps.
 */
export function MenuLoadError({ error, phoneNumber, onRetry }: Props) {
  if (isTransportFailure(error)) {
    return <ConnectionErrorState onRetry={onRetry} />;
  }

  const phone = toCallablePhone(phoneNumber);

  return (
    <ConnectionErrorState
      heading={MENU_LOAD_ERROR_COPY.heading}
      message={
        phone ? (
          <>
            {MENU_LOAD_ERROR_COPY.messageWithPhone}{' '}
            <CallLink href={phone.href}>{phone.display}</CallLink>
          </>
        ) : (
          MENU_LOAD_ERROR_COPY.messageWithoutPhone
        )
      }
      onRetry={onRetry}
    />
  );
}
